import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { DropdownMenu, TableList, TableListActions, Typography, useOverlayContext } from 'seagull';
import { CopyFill, Edit1Liner24, TrashLiner24 } from 'seagull/icons';
import { routes } from '~/constants';
import { useDuplicateRelationship } from '~/hooks/useDuplicateRelationship';
import { useScheduledProposal } from '~/hooks/useScheduledProposal';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { formatDateWithTime } from '~/models/time/selectors';
import AdditionalRecipientsCell from './draftCells/AdditionalRecipientsCell';
import AgreementNameCell from './draftCells/AgreementNameCell';
import AgreementNoteCell from './draftCells/AgreementNoteCell';
import AutoApproveAmendmentsCell from './draftCells/AutoApproveAmendments';
import AutoApproveOutOfScopeChargesCell from './draftCells/AutoApproveOutOfScopeChargesCell';
import ContactCell from './draftCells/ContactCell';
import CreditCardProcessingFeesCell from './draftCells/CreditCardProcessingFeesCell';
import EffectiveDateCell from './draftCells/EffectiveDateCell';
import ExpirationDateCell from './draftCells/ExpirationDateCell';
import IntroductoryMessageCell from './draftCells/IntroductoryMessageCell';
import IntroductoryVideoCell from './draftCells/IntroductoryVideoCell';
import ManualPaymentApprovalCell from './draftCells/ManualPaymentApprovalCell';
import NetTermsCell from './draftCells/NetTermsCell';
import PaymentMethodRequiredCell from './draftCells/PaymentMethodRequiredCell';
import TermsOfServiceCell from './draftCells/TermsOfServiceCell';
import DraftUpdateModal from './DraftUpdateModal';
import EditDraftServiceTable from './EditDraftServiceTable/EditDraftServiceTable';
import { getModifiedBy, makeEditableTableItem } from './EditDraftsTable.utils';
import styles from './EditDraftsTableRow.module.scss';
import { useUpdateDraft } from './hooks/useUpdateDraft';
import { EditDraftsTableColumns } from './types';
const testIdPrefix = 'edit-drafts-table';
const EditDraftsTableRow = (props)=>{
    const { index, visibleColumns, draft, getContact, updateDraft, deleteDraft } = props;
    const history = useHistory();
    const { duplicateDraftFromId } = useDuplicateRelationship();
    const { terminateDraftById } = useTerminateAgreement();
    const contact = getContact(draft);
    const { showModal, startLoader, stopLoader } = useOverlayContext();
    const { updateDraft: saveDraft } = useUpdateDraft();
    const isDisabledForScheduledDrafts = draft.isScheduled();
    const { showSwitchToEditModal } = useScheduledProposal({
        draftId: draft.id(),
        refresh: ()=>{},
        isSampleRelationship: draft.isSample()
    });
    const editDraft = ()=>{
        if (isDisabledForScheduledDrafts) {
            showSwitchToEditModal();
        } else {
            history.push(routes.draft(draft.id()));
        }
    };
    const openDraftUpdateModal = (param)=>{
        let { width, maxHeight, renderContent, shouldSubmit, skin, submitOnNotDirty } = param;
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(DraftUpdateModal, {
                    skin: skin,
                    draft: draft,
                    width: width,
                    maxHeight: maxHeight,
                    renderContent: renderContent,
                    dismissModal: dismissModal,
                    changeDraftInRow: updateDraft,
                    shouldSubmit: shouldSubmit,
                    submitOnNotDirty: submitOnNotDirty
                });
            }
        });
    };
    const EditableCellDefaultProps = {
        draft,
        index,
        onOpenModal: openDraftUpdateModal,
        isDisabled: isDisabledForScheduledDrafts,
        disabledTooltip: isDisabledForScheduledDrafts ? 'In order to edit a scheduled proposal, its scheduling must be canceled.' : undefined
    };
    function onAddServices(services, packageId) {
        const newDraft = draft.addServices(services, packageId);
        const loaderName = 'addServicesLoader';
        saveDraft({
            newDraft,
            onSuccess: ()=>updateDraft(newDraft),
            onStartLoader: ()=>startLoader(loaderName),
            onStopLoader: ()=>stopLoader(loaderName)
        });
    }
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: draft.id(),
        testId: "".concat(testIdPrefix, "-row-").concat(draft.id()),
        items: compact([
            makeEditableTableItem(/*#__PURE__*/ _jsx(ContactCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                getContact: getContact
            }))),
            visibleColumns[EditDraftsTableColumns.AdditionalRecipients] && makeEditableTableItem(/*#__PURE__*/ _jsx(AdditionalRecipientsCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact
            }))),
            visibleColumns[EditDraftsTableColumns.AgreementName] && makeEditableTableItem(/*#__PURE__*/ _jsx(AgreementNameCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.EffectiveDate] && makeEditableTableItem(/*#__PURE__*/ _jsx(EffectiveDateCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.ModifiedBy] && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    testId: "last-modified-by-".concat(index),
                    children: getModifiedBy(draft)
                })
            },
            visibleColumns[EditDraftsTableColumns.LastModified] && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    testId: "last-modified-on-".concat(index),
                    children: formatDateWithTime(draft.lastModifiedOn())
                })
            },
            visibleColumns[EditDraftsTableColumns.ExpirationDate] && makeEditableTableItem(/*#__PURE__*/ _jsx(ExpirationDateCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.IntroductoryMessage] && makeEditableTableItem(/*#__PURE__*/ _jsx(IntroductoryMessageCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.IntroductoryVideo] && makeEditableTableItem(/*#__PURE__*/ _jsx(IntroductoryVideoCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.TermsOfService] && makeEditableTableItem(/*#__PURE__*/ _jsx(TermsOfServiceCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.AgreementNote] && makeEditableTableItem(/*#__PURE__*/ _jsx(AgreementNoteCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.AutoApproveAmendments] && makeEditableTableItem(/*#__PURE__*/ _jsx(AutoApproveAmendmentsCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.AutoApproveOutOfScopeCharges] && makeEditableTableItem(/*#__PURE__*/ _jsx(AutoApproveOutOfScopeChargesCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue] && makeEditableTableItem(/*#__PURE__*/ _jsx(NetTermsCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.PaymentMethodRequiredForSigning] && makeEditableTableItem(/*#__PURE__*/ _jsx(PaymentMethodRequiredCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.CreditCardProcessingFees] && makeEditableTableItem(/*#__PURE__*/ _jsx(CreditCardProcessingFeesCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact
            }))),
            visibleColumns[EditDraftsTableColumns.ManualApprovalOfEachPayment] && makeEditableTableItem(/*#__PURE__*/ _jsx(ManualPaymentApprovalCell, _object_spread({}, EditableCellDefaultProps)))
        ]),
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            testId: "table-list-actions-".concat(draft.id()),
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(Edit1Liner24, {}),
                    onClick: editDraft,
                    testId: "table-list-actions-edit-".concat(draft.id()),
                    children: "Edit Draft"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(CopyFill, {}),
                    onClick: ()=>duplicateDraftFromId(draft.id()),
                    testId: "table-list-actions-duplicate-".concat(draft.id()),
                    children: "Duplicate Draft"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(TrashLiner24, {}),
                    onClick: ()=>terminateDraftById(draft.id(), ()=>deleteDraft(draft)),
                    testId: "table-list-actions-discard-".concat(draft.id()),
                    children: "Discard Draft"
                })
            ]
        }),
        expandedContent: ()=>/*#__PURE__*/ _jsx("div", {
                className: styles.expandedContent,
                "data-testid": "expanded-content-".concat(draft.id()),
                children: /*#__PURE__*/ _jsx(EditDraftServiceTable, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                    onAddServices: onAddServices
                }))
            })
    });
};
export default EditDraftsTableRow;
